import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Card } from '../../styles/GlobalStyles';

const SuccessContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: var(--spacing-md);
`;

const SuccessCard = styled(Card)`
  width: 100%;
  max-width: 500px;
  margin: 0;
  text-align: center;
`;

const IconContainer = styled.div`
  font-size: 4rem;
  margin-bottom: var(--spacing-lg);
`;

const Title = styled.h1`
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
`;

const Message = styled.p`
  margin-bottom: var(--spacing-lg);
  color: var(--text-secondary);
  line-height: 1.6;
`;

const InfoBox = styled.div`
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  text-align: left;
  
  h3 {
    margin: 0 0 var(--spacing-sm) 0;
    color: var(--text-primary);
    font-size: 1rem;
    font-weight: 600;
  }
  
  p {
    margin: var(--spacing-xs) 0;
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: var(--spacing-lg) auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/administration-club');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  if (!sessionId) {
    return (
      <SuccessContainer>
        <SuccessCard>
          <IconContainer>⚠️</IconContainer>
          <Title>Invalid Session</Title>
          <Message>
            No payment session found. Please try registering again.
          </Message>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  return (
    <SuccessContainer>
      <SuccessCard>
        <IconContainer>✅</IconContainer>
        <Title>Payment Successful!</Title>
        <Message>
          Thank you for subscribing to GamePath. Your payment has been processed successfully.
        </Message>

        <InfoBox>
          <h3>📧 Check Your Email</h3>
          <p>
            We've sent a verification email to your registered email address.
          </p>
          <p>
            <strong>Please verify your email to activate your account and access the dashboard.</strong>
          </p>
          <p style={{ marginTop: 'var(--spacing-sm)' }}>
            Didn't receive the email? Check your spam folder or contact support.
          </p>
        </InfoBox>

        <Message style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Redirecting to login page in {countdown} seconds...
        </Message>
        
        <LoadingSpinner />
      </SuccessCard>
    </SuccessContainer>
  );
};
